import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import HowToSteps, { HowToStep } from '@comp/learn/how-to-steps'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

interface PageContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  badges: string[]
  primaryCta: string
  secondaryCta: string
  stepsEyebrow: string
  stepsTitle: string
  stepsBody: string
  docLabel: string
  steps: HowToStep[]
  relatedEyebrow: string
  relatedTitle: string
  related: RelatedItem[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    metaTitle: 'Google Cloud console SSO with Workforce Identity Federation',
    metaDescription:
      'Sign your users into the Google Cloud console and gcloud CLI with their Monosign identity: no Google accounts, no user sync. OIDC setup, attribute mapping, and IAM grants, step by step.',
    heroEyebrow: 'How-to · Google Cloud SSO',
    heroTitle: ['Google Cloud console SSO.', 'No Google accounts.'],
    heroBody:
      'Workforce Identity Federation lets your users open the Google Cloud console and gcloud CLI with their Monosign identity, local or synced from Active Directory. No user is created on the Google side: every sign-in maps to a workforce principal, and IAM roles attach to your Monosign groups.',
    badges: ['OIDC · authorization code + PKCE', 'Console + gcloud CLI', 'Verified end to end'],
    primaryCta: 'Start free trial →',
    secondaryCta: 'Open the docs',
    stepsEyebrow: 'Walkthrough',
    stepsTitle: 'From OIDC key to console session.',
    stepsBody: 'Five steps on two consoles. Group-based IAM from day one.',
    docLabel: 'Continue in the full docs',
    steps: [
      {
        title: 'Create the application and OIDC key in Monosign',
        body: 'Add a Web application (URL: the Google Cloud console) and create an OIDC/OpenID key on it. The Client Id and Client Secret go to Google in step 3. Set the signature algorithm to RS256 and the scope list to openid email profile.',
        hint: 'Pin the global JWT issuer and configure an organization signing certificate first: Google validates tokens against your JWKS, and an ephemeral key would break the federation on every restart.',
      },
      {
        title: 'Make the subject stable and map groups',
        body: 'Set the key’s Subject (sub) Format to the stable Monosign User Id (or add a Claim Mapping row sub → {{UserId}} on earlier versions), and enable Group Mapping with the attribute name "groups". Google builds the workforce principal from sub; a username-based subject would orphan all IAM grants the day a user is renamed.',
        hint: "Google requires the sub in the userinfo response to equal the sub in the ID token; the stable-Id configuration satisfies both, and the sign-in fails with a clear error when it is missing.",
      },
      {
        title: 'Create the workforce pool and OIDC provider in Google Cloud',
        body: 'With the organisation-level workforce pool admin role, create a workforce pool and an OIDC provider pointing at your Monosign issuer, with the Client Id/Secret, response type "code", and an attribute mapping that carries google.subject, google.groups, and your email/username attributes.',
        hint: 'Workforce pools are organisation resources: a project role is not enough, and a freshly granted org role can take a few minutes to propagate.',
      },
      {
        title: 'Grant IAM roles to Monosign groups',
        body: 'Bind roles to principalSet://…/group/<Monosign group name> rather than to individual users. Membership then lives in Monosign: a user removed from the group loses the role at their next sign-in. An attribute condition on the provider can additionally restrict who may enter the pool at all.',
      },
      {
        title: 'Test the console and the CLI',
        body: 'Open the pool’s sign-in URL: Google redirects to Monosign, your policy runs, and the user lands in the console. For the CLI, generate a login-config file once (it contains no secrets) and have users run gcloud auth login with it.',
        hint: 'Keep the pool session duration short, around an hour. Google holds its session for that long even after the Monosign session ends.',
      },
    ],
    relatedEyebrow: 'Keep exploring',
    relatedTitle: 'Related Monofor capabilities.',
    related: [
      { label: 'Keyless GCP for workloads', description: 'The companion guide: Workload Identity Federation.', href: '/learn/gcp-workload-identity-federation' },
      { label: 'Single Sign-On', description: 'The SSO capability behind every federation.', href: '/sign/sso' },
      { label: 'Integrations catalog', description: 'Every Monofor integration in one place.', href: '/integrations' },
    ],
  },
  tr: {
    metaTitle: 'Workforce Identity Federation ile Google Cloud konsoluna SSO',
    metaDescription:
      "Kullanıcılarınız Google Cloud konsoluna ve gcloud CLI'ya Monosign kimliğiyle girsin: Google hesabı yok, kullanıcı senkronu yok. OIDC kurulumu, öznitelik eşleme ve IAM yetkileri adım adım.",
    heroEyebrow: 'Rehber · Google Cloud SSO',
    heroTitle: ['Google Cloud konsoluna SSO.', 'Google hesabı olmadan.'],
    heroBody:
      "Workforce Identity Federation, kullanıcılarınızın Google Cloud konsolunu ve gcloud CLI'yı Monosign kimliğiyle (yerel ya da Active Directory'den senkronlanmış) açmasını sağlar. Google tarafında kullanıcı oluşmaz: her giriş bir workforce principal'a eşlenir, IAM rolleri Monosign gruplarınıza bağlanır.",
    badges: ['OIDC · authorization code + PKCE', 'Konsol + gcloud CLI', 'Uçtan uca doğrulandı'],
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'Dokümantasyonu açın',
    stepsEyebrow: 'Adım adım',
    stepsTitle: "OIDC anahtarından konsol oturumuna.",
    stepsBody: 'İki konsolda beş adım. İlk günden grup tabanlı IAM.',
    docLabel: 'Rehberin tamamına gidin',
    steps: [
      {
        title: "Monosign'da uygulamayı ve OIDC anahtarını oluşturun",
        body: "Bir Web uygulaması ekleyin (URL: Google Cloud konsolu) ve üzerinde bir OIDC/OpenID anahtarı oluşturun. Client Id ve Client Secret 3. adımda Google'a girilecek. İmza algoritmasını RS256, scope listesini openid email profile yapın.",
        hint: 'Önce global JWT issuer değerini sabitleyin ve organizasyon imza sertifikası tanımlayın: Google token imzasını JWKS üzerinden doğrular; geçici anahtar her yeniden başlatmada federasyonu kırar.',
      },
      {
        title: "Subject'i kalıcı yapın ve grupları eşleyin",
        body: "Anahtarın Subject (sub) Format ayarını kalıcı Monosign User Id yapın (eski sürümlerde Claim Mapping'e sub → {{UserId}} satırı ekleyin) ve Group Mapping'i \"groups\" öznitelik adıyla açın. Google, workforce principal'ı sub'dan kurar; kullanıcı adına dayalı subject, kullanıcı yeniden adlandırıldığı gün tüm IAM yetkilerini sahipsiz bırakır.",
        hint: "Google, userinfo yanıtındaki sub'ın ID token'daki sub ile aynı olmasını şart koşar; kalıcı-Id yapılandırması ikisini birden karşılar, eksikse giriş net bir hatayla düşer.",
      },
      {
        title: "Google Cloud'da workforce pool ve OIDC provider'ı oluşturun",
        body: 'Organizasyon düzeyindeki workforce pool admin rolüyle bir workforce pool ve Monosign issuer’ınıza işaret eden bir OIDC provider oluşturun: Client Id/Secret, response type "code" ve google.subject, google.groups ile e-posta/kullanıcı adı özniteliklerini taşıyan bir attribute mapping.',
        hint: 'Workforce pool bir organizasyon kaynağıdır: proje rolü yetmez; yeni verilmiş organizasyon rolünün yayılması birkaç dakika sürebilir.',
      },
      {
        title: 'IAM rollerini Monosign gruplarına verin',
        body: "Rolleri tek tek kullanıcılara değil principalSet://…/group/<Monosign grup adı> ilkesine bağlayın. Üyelik böylece Monosign'da yaşar: gruptan çıkarılan kullanıcı bir sonraki girişte rolü kaybeder. Provider'daki attribute condition, havuza kimin girebileceğini ayrıca daraltabilir.",
      },
      {
        title: 'Konsolu ve CLI’yı test edin',
        body: "Havuzun giriş URL'ini açın: Google, Monosign'a yönlendirir, politikanız çalışır ve kullanıcı konsola düşer. CLI için bir kez login-config dosyası üretin (sır içermez) ve kullanıcılar gcloud auth login'i bu dosyayla çalıştırsın.",
        hint: "Havuz oturum süresini kısa tutun, bir saat civarı. Monosign oturumu bitse bile Google kendi oturumunu o süre boyunca sürdürür.",
      },
    ],
    relatedEyebrow: 'Keşfetmeye devam edin',
    relatedTitle: 'İlgili Monofor yetenekleri.',
    related: [
      { label: 'Workload için anahtarsız GCP', description: 'Eş rehber: Workload Identity Federation.', href: '/learn/gcp-workload-identity-federation' },
      { label: 'Tek Oturum Açma (SSO)', description: 'Her federasyonun arkasındaki SSO yeteneği.', href: '/sign/sso' },
      { label: 'Entegrasyon kataloğu', description: 'Tüm Monofor entegrasyonları tek yerde.', href: '/integrations' },
    ],
  },
}

type Props = { locale: string }

export default function GcpWorkforceFederationPage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/learn/gcp-workforce-identity-federation"
        locale={locale}
        bilingual
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          badges={c.badges}
          primaryCta={{ label: c.primaryCta, href: '/try/now' }}
          secondaryCta={{ label: c.secondaryCta, href: 'https://help.monofor.com/monosign' }}
        />
        <HowToSteps
          eyebrow={c.stepsEyebrow}
          title={<>{c.stepsTitle}</>}
          body={c.stepsBody}
          steps={c.steps}
          schemaName={c.metaTitle}
          docHref="https://help.monofor.com/monosign"
          docLabel={c.docLabel}
        />
        <SolutionRelated
          eyebrow={c.relatedEyebrow}
          title={c.relatedTitle}
          items={c.related}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
